import { getPosts, getProjects } from '@/lib/content';
import PageMeta from '@/shared/components/PageMeta';
import AcademicLayout from '@/shared/components/AcademicLayout';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { formatDate } from '@/shared/utils/i18n';
import { Button } from '@/shared/ui/button';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Lightbulb, Target, Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

// Tailwind 只能识别静态出现的完整类名，颜色变体必须写成映射表
const areaStyles = {
  yellow: { chip: 'bg-yellow-500/10 group-hover:bg-yellow-500/15', icon: 'text-yellow-600 dark:text-yellow-400' },
  green: { chip: 'bg-green-500/10 group-hover:bg-green-500/15', icon: 'text-green-600 dark:text-green-400' },
  blue: { chip: 'bg-blue-500/10 group-hover:bg-blue-500/15', icon: 'text-blue-600 dark:text-blue-400' },
  orange: { chip: 'bg-orange-500/10 group-hover:bg-orange-500/15', icon: 'text-orange-600 dark:text-orange-400' }
} as const;

const postBarStyles = [
  'bg-blue-500/20 group-hover:bg-blue-500/40',
  'bg-green-500/20 group-hover:bg-green-500/40',
  'bg-orange-500/20 group-hover:bg-orange-500/40'
] as const;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

export default function HomePage() {
  const { language, t } = useLanguage();

  const recentPosts = useMemo(() => getPosts(language).slice(0, 3), [language]);
  const currentProjects = useMemo(
    () => getProjects(language).filter(project => project.meta.status === 'active').slice(0, 2),
    [language]
  );

  const researchAreas = [
    { icon: BookOpen, title: t('history'), desc: t('historyDesc'), color: 'yellow' as const },
    { icon: Users, title: t('politics'), desc: t('politicsDesc'), color: 'green' as const },
    { icon: Target, title: t('economics'), desc: t('economicsDesc'), color: 'blue' as const },
    { icon: Lightbulb, title: t('interdisciplinary'), desc: t('interdisciplinaryDesc'), color: 'orange' as const }
  ];

  return (
    <AcademicLayout>
      <PageMeta title="About" description="Kaibin Zhang — History, Politics and Economics at UCL. Full-stack developer, robotics builder." path="/" />
      <motion.div className="space-y-24" variants={containerVariants} initial="hidden" animate="visible">
        {/* 个人简介 */}
        <motion.section variants={itemVariants}>
          <h2 className="text-2xl font-light text-foreground mb-10">{t('aboutMeTitle')}</h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-base text-muted-foreground mb-6 leading-relaxed">{t('aboutMeContent')}</p>
            <p className="text-sm text-muted-foreground/80 mb-8 leading-relaxed">{t('researchFocus')}</p>
            <div className="space-y-3">
              {[t('researchItem1'), t('researchItem2'), t('researchItem3'), t('researchItem4')].map(
                (item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-2"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <span className="w-1 h-1 bg-muted-foreground/40 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span className="text-sm text-muted-foreground/80">{item}</span>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </motion.section>

        {/* 研究领域 */}
        <motion.section variants={itemVariants}>
          <h2 className="text-2xl font-light text-foreground mb-10">{t('researchAreas')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {researchAreas.map((area, index) => (
              <motion.div
                key={index}
                className="p-6 rounded-xl border border-border/50 hover:border-border/80 transition-all duration-300 group bg-card/30 hover:bg-card/50"
                whileHover={{ y: -1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-lg ${areaStyles[area.color].chip} transition-colors flex items-center justify-center shrink-0`}
                  >
                    <area.icon className={`w-5 h-5 ${areaStyles[area.color].icon}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-foreground mb-2">{area.title}</h3>
                    <p className="text-sm text-muted-foreground/80 leading-relaxed">{area.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* 最新文章（来自内容系统） */}
        <motion.section variants={itemVariants}>
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-light text-foreground">{t('recentPosts')}</h2>
            <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <Link to="/posts" className="inline-flex items-center gap-1.5">
                {t('viewAllPosts')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
          <div className="space-y-6">
            {recentPosts.map((post, index) => (
              <motion.article
                key={post.slug}
                className="group p-4 -mx-4 rounded-lg hover:bg-muted/20 transition-all duration-300"
                whileHover={{ x: 4 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Link to={`/posts/${post.slug}`} className="flex items-start gap-4">
                  <div
                    className={`w-0.5 h-16 rounded-full transition-all duration-300 ${postBarStyles[index % postBarStyles.length]}`}
                  />
                  <div className="flex-1 space-y-2">
                    <h3 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                      {post.meta.title}
                    </h3>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-muted-foreground">{formatDate(post.meta.date, language)}</span>
                      {post.meta.category && (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {post.meta.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground/80 leading-relaxed">{post.meta.summary}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </motion.section>

        {/* 当前项目（来自内容系统） */}
        <motion.section variants={itemVariants}>
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-light text-foreground">{t('currentProjects')}</h2>
            <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <Link to="/project" className="inline-flex items-center gap-1.5">
                {t('viewAllProjects')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentProjects.map(project => (
              <motion.div
                key={project.slug}
                className="p-6 rounded-xl border border-border/50 hover:border-border/80 transition-all duration-300 bg-card/30 hover:bg-card/50"
                whileHover={{ y: -1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-medium text-foreground">{project.meta.title}</h3>
                    <span className="px-2.5 py-1 text-xs rounded-md bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 font-medium shrink-0">
                      {t('statusActive')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground/80 leading-relaxed">{project.meta.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.meta.stack.slice(0, 4).map(item => (
                      <span
                        key={item}
                        className="px-2.5 py-1 text-xs bg-muted/30 text-foreground rounded-md border border-border/30"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </motion.div>
    </AcademicLayout>
  );
}
